import { assert, isDefined } from '@sama/js-core';
import { Quaternion, Ray, Vector2, Vector3 } from 'three';
import { getCalibrationDataForFrame } from '../../../cross-team-shared/misc-utils/projection-utils';
import { SettingState } from '../../../cross-team-shared/state/setting-state';
import {
  Point2D,
  Point3D,
} from '../../../team-apis/otterdam/answers/shape-typings';
import { PointCloudContentState } from '../../state/point-cloud-content-state';
import { pointToVector } from '../point-cloud-utils';
import {
  AbstractProjectionUtils,
  ProjectTo2DOptions,
} from './abstract-projection-utils';

export class CdiLutProjectionUtils extends AbstractProjectionUtils {
  private frameRotation = new Quaternion();

  projectTo2D(
    _point: Point3D,
    _settingState: SettingState,
    _state: PointCloudContentState,
    _options: ProjectTo2DOptions,
  ): { position: Point2D; isInsideFrustum: boolean } {
    throw new Error('2D projection not supported for LUTs');
  }

  projectTo3D(
    pixel: Vector2,
    state: PointCloudContentState,
    outputName: string,
  ) {
    const frameCalibrationData = getCalibrationDataForFrame(
      state.render.frameNumber,
      state.calibrationData[outputName],
    );

    const lightray = this.getLightrayFromPixel(pixel, state);
    this.frameRotation.fromArray(frameCalibrationData.rotation);
    lightray.applyQuaternion(this.frameRotation);

    return new Ray(pointToVector(frameCalibrationData.position), lightray);
  }

  private getLightrayFromPixel(pixel: Vector2, state: PointCloudContentState) {
    const lutData = state.render.lutData;
    assert(
      lutData?.pixelToLightray,
      'Cannot get Lightray unless we have lightray LUT',
    );

    const lightrayLength = 3;

    const offset =
      lightrayLength * (pixel.y * lutData.metadata.image_width + pixel.x);

    const x = lutData.pixelToLightray[offset];
    const y = lutData.pixelToLightray[offset + 1];
    const z = lutData.pixelToLightray[offset + 2];

    assert(
      isDefined(x) && isDefined(y) && isDefined(z),
      `Could not find Lightray value for point: ${pixel.toArray()}`,
    );

    return new Vector3(x, y, z);
  }
}
